import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, ExternalLink, X, ZoomIn, ZoomOut } from "lucide-react";
import { Link } from "react-router-dom";
import { getOptimizedImageUrl } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useLocalized } from "@/hooks/useLocalized";
import { LocalizedPortfolioAlbum } from "@/types";

type PortfolioItem = LocalizedPortfolioAlbum;

interface PortfolioCarouselProps {
  portfolioItems: PortfolioItem[];
}

export function PortfolioCarousel({ portfolioItems }: PortfolioCarouselProps) {
  const { t } = useTranslation();
  const { getLocalized } = useLocalized();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentAlbum, setCurrentAlbum] = useState<PortfolioItem | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);

  const thumbnailRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % portfolioItems.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + portfolioItems.length) % portfolioItems.length);
  };

  const openLightbox = (album: PortfolioItem, imageIndex: number = 0) => {
    setCurrentAlbum(album);
    setCurrentImageIndex(imageIndex);
    setLightboxOpen(true);
    setIsZoomed(false);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentAlbum(null);
    setCurrentImageIndex(0);
    setIsZoomed(false);
    document.body.style.overflow = 'unset';
  };

  const goToNext = () => {
    if (!currentAlbum) return;
    setIsZoomed(false);
    setCurrentImageIndex((prev) =>
      prev === (currentAlbum.portfolio_media?.length || 1) - 1 ? 0 : prev + 1
    );
  };

  const goToPrevious = () => {
    if (!currentAlbum) return;
    setIsZoomed(false);
    setCurrentImageIndex((prev) =>
      prev === 0 ? (currentAlbum.portfolio_media?.length || 1) - 1 : prev - 1
    );
  };

  const toggleZoom = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setIsZoomed(!isZoomed);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;

      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrevious();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentAlbum, currentImageIndex]);

  // Scroll active thumbnail into view
  useEffect(() => {
    if (lightboxOpen && currentAlbum) {
      const activeThumbnail = thumbnailRefs.current.get(currentImageIndex);
      if (activeThumbnail) {
        activeThumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [currentImageIndex, lightboxOpen, currentAlbum]);

  // Touch handlers for carousel swipe
  const handleCarouselTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleCarouselTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleCarouselTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Touch handlers for lightbox swipe - Only allow swipe if NOT zoomed
  const handleLightboxTouchStart = (e: React.TouchEvent) => {
    if (isZoomed) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleLightboxTouchMove = (e: React.TouchEvent) => {
    if (isZoomed) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleLightboxTouchEnd = () => {
    if (isZoomed || !touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) goToNext();
    if (isRightSwipe) goToPrevious();
  };

  if (!portfolioItems.length) {
    return (
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">{t('portfolio.featuredTitle', 'Featured Work')}</h2>
          <p className="text-sm sm:text-base text-muted-foreground">{t('portfolio.comingSoon', 'Portfolio coming soon...')}</p>
        </div>
      </section>
    );
  }

  const currentCarouselAlbum = portfolioItems[currentIndex];
  const albumMedia = currentCarouselAlbum.portfolio_media?.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)) || [];
  const currentMedia = currentAlbum?.portfolio_media?.[currentImageIndex];

  return (
    <section className="py-12 sm:py-16 md:py-20 px-4 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            {t('portfolio.featuredTitle', 'Featured Work')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            {t('portfolio.featuredSubtitle', 'A showcase of my finest photography work across various styles and occasions')}
          </p>
        </div>

        <div className="relative">
          {/* Main Carousel - Shows Album Cover */}
          <div
            className="relative h-[50vh] sm:h-[60vh] md:h-[70vh] overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl shadow-[var(--shadow-elegant)] cursor-pointer group"
            onTouchStart={handleCarouselTouchStart}
            onTouchMove={handleCarouselTouchMove}
            onTouchEnd={handleCarouselTouchEnd}
            onClick={() => openLightbox(currentCarouselAlbum, 0)}
          >
            <div className="relative w-full h-full">
              <img
                src={getOptimizedImageUrl(currentCarouselAlbum.cover_image_url, { width: 1200, quality: 90 })}
                alt={getLocalized(currentCarouselAlbum.title)}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                loading="eager"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

              {/* Content Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8 text-white">
                <div className="max-w-2xl">
                  {currentCarouselAlbum.category && (
                    <Badge variant="secondary" className="mb-2 sm:mb-3 md:mb-4 capitalize text-xs sm:text-sm">
                      {t(`category.${currentCarouselAlbum.category}`, currentCarouselAlbum.category)}
                    </Badge>
                  )}
                  <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 md:mb-4">
                    {getLocalized(currentCarouselAlbum.title)}
                  </h3>
                  {currentCarouselAlbum.description && (
                    <p className="text-sm sm:text-base md:text-lg opacity-90 mb-3 sm:mb-4 md:mb-6 line-clamp-2">
                      {getLocalized(currentCarouselAlbum.description)}
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-white/30 bg-white/90 text-black hover:bg-white hover:text-black backdrop-blur-sm text-xs sm:text-sm md:text-base pointer-events-none"
                  >
                    {t('portfolio.viewCollection', 'View Collection')}
                    <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <Button
              variant="outline"
              size="icon"
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm h-8 w-8 sm:h-10 sm:w-10 z-10"
              onClick={(e) => {
                e.stopPropagation();
                prevSlide();
              }}
            >
              <ChevronLeft className="h-4 w-4 sm:h-6 sm:w-6" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm h-8 w-8 sm:h-10 sm:w-10 z-10"
              onClick={(e) => {
                e.stopPropagation();
                nextSlide();
              }}
            >
              <ChevronRight className="h-4 w-4 sm:h-6 sm:w-6" />
            </Button>
          </div>

          {/* Thumbnail Navigation - Shows Album Images */}
          {albumMedia.length > 0 && (
            <div className="flex justify-center gap-1.5 sm:gap-2 mt-4 sm:mt-6 overflow-x-auto pb-2 px-2">
              {albumMedia.map((media, index) => (
                <button
                  key={media.id}
                  onClick={() => openLightbox(currentCarouselAlbum, index)}
                  className="relative w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-md sm:rounded-lg overflow-hidden flex-shrink-0 transition-all duration-300 opacity-70 hover:opacity-100 hover:scale-105"
                >
                  <img
                    src={getOptimizedImageUrl(media.media_url, { width: 100, height: 100, resize: 'cover' })}
                    alt={getLocalized(media.caption) || "Album image"}
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="text-center mt-8 sm:mt-10 md:mt-12">
          <Button size="lg" className="px-6 sm:px-8" asChild>
            <Link to="/portfolio">{t('portfolio.viewFull', 'View Full Portfolio')}</Link>
          </Button>
        </div>
      </div>

      {/* Full-Screen Lightbox */}
      {lightboxOpen && currentAlbum && currentMedia && (
        <div
          className="fixed inset-0 bg-black z-50 flex flex-col"
          onTouchStart={handleLightboxTouchStart}
          onTouchMove={handleLightboxTouchMove}
          onTouchEnd={handleLightboxTouchEnd}
        >
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4 transition-opacity duration-300 pointer-events-none group-hover:opacity-100">
            <div className="flex items-center justify-between max-w-7xl mx-auto pointer-events-auto">
              <div className="text-white">
                <h3 className="text-lg md:text-xl font-semibold">{getLocalized(currentAlbum.title)}</h3>
                <p className="text-sm text-white/80">
                  {currentImageIndex + 1} / {currentAlbum.portfolio_media?.length || 0}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => toggleZoom(e)}
                  className="text-white hover:bg-white/20"
                >
                  {isZoomed ? <ZoomOut className="h-6 w-6" /> : <ZoomIn className="h-6 w-6" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeLightbox}
                  className="text-white hover:bg-white/20"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>
          </div>

          {/* Main Image */}
          <div
            className={`flex-1 flex items-center justify-center p-4 md:p-8 overflow-auto ${isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
            onClick={(e) => toggleZoom(e)}
          >
            <img
              src={getOptimizedImageUrl(currentMedia.media_url, { width: 1920, quality: 95 })}
              alt={getLocalized(currentMedia.caption) || getLocalized(currentAlbum.title)}
              className={`transition-all duration-300 ${isZoomed
                ? 'max-w-none max-h-none min-w-full'
                : 'max-w-full max-h-full object-contain'
                }`}
              loading="eager"
            />
          </div>

          {/* Navigation Arrows (Hidden during zoom) */}
          {!isZoomed && (currentAlbum.portfolio_media?.length || 0) > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12 md:h-16 md:w-16"
              >
                <ChevronLeft className="h-8 w-8 md:h-10 md:w-10" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12 md:h-16 md:w-16"
              >
                <ChevronRight className="h-8 w-8 md:h-10 md:w-10" />
              </Button>
            </>
          )}

          {/* Caption (Hidden during zoom) */}
          {!isZoomed && currentMedia.caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-6 pointer-events-none">
              <p className="text-white text-center text-sm md:text-base max-w-3xl mx-auto">
                {getLocalized(currentMedia.caption)}
              </p>
            </div>
          )}

          {/* Thumbnail Strip (Hidden during zoom) */}
          {!isZoomed && (
            <div className="absolute bottom-20 md:bottom-24 left-0 right-0 overflow-x-auto">
              <div className="flex gap-2 px-4 justify-center">
                {currentAlbum.portfolio_media?.map((media, index) => (
                  <button
                    key={media.id}
                    ref={(el) => {
                      if (el) thumbnailRefs.current.set(index, el);
                      else thumbnailRefs.current.delete(index);
                    }}
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                    className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded overflow-hidden border-2 transition-all ${index === currentImageIndex
                      ? 'border-white scale-110'
                      : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                  >
                    <img
                      src={getOptimizedImageUrl(media.media_url, { width: 100, height: 100, resize: 'cover' })}
                      alt=""
                      className="w-full h-full object-cover object-center"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}